import resolveReduction, {} from './resolveReduction.js';
import { spanOfBox, weightInSpan } from './boxSpan.js';
import VoxelStatistics from '../../enums/VoxelStatistics.js';
import { requireVoxelStatistic } from './voxelStatistics.js';
function createAccumulators(statistic) {
    return { definition: requireVoxelStatistic(statistic), perComponent: [] };
}
function growAccumulators(accumulators, count) {
    const { definition, perComponent } = accumulators;
    while (perComponent.length < count) {
        perComponent.push(definition.createAccumulator());
    }
}
function valueOfBox(source, resolved, accumulators, i, j, k) {
    const { factors, sourceOffset, sourceEnd } = resolved;
    const { perComponent } = accumulators;
    const spanI = spanOfBox(sourceOffset[0], factors[0], sourceEnd[0], i);
    const spanJ = spanOfBox(sourceOffset[1], factors[1], sourceEnd[1], j);
    const spanK = spanOfBox(sourceOffset[2], factors[2], sourceEnd[2], k);
    for (let component = 0; component < perComponent.length; component++) {
        perComponent[component].reset();
    }
    let components = 0;
    for (let sourceK = spanK.first; sourceK < spanK.end; sourceK++) {
        const weightK = weightInSpan(spanK, sourceK);
        for (let sourceJ = spanJ.first; sourceJ < spanJ.end; sourceJ++) {
            const weightJK = weightK * weightInSpan(spanJ, sourceJ);
            for (let sourceI = spanI.first; sourceI < spanI.end; sourceI++) {
                const weight = weightJK * weightInSpan(spanI, sourceI);
                const value = source.getAtIJK(sourceI, sourceJ, sourceK);
                if (typeof value === 'number') {
                    if (Number.isFinite(value)) {
                        growAccumulators(accumulators, 1);
                        perComponent[0].add(value, weight);
                    }
                    continue;
                }
                if (!value) {
                    continue;
                }
                const length = value.length;
                growAccumulators(accumulators, length);
                if (length > components) {
                    components = length;
                }
                for (let component = 0; component < length; component++) {
                    const componentValue = value[component];
                    if (Number.isFinite(componentValue)) {
                        perComponent[component].add(componentValue, weight);
                    }
                }
            }
        }
    }
    if (components === 0) {
        return perComponent.length === 0 ? undefined : perComponent[0].getValue();
    }
    const result = [];
    for (let component = 0; component < components; component++) {
        const componentValue = perComponent[component].getValue();
        if (componentValue === undefined) {
            return undefined;
        }
        result.push(componentValue);
    }
    return result;
}
function roundValue(value) {
    if (typeof value === 'number') {
        return Math.round(value);
    }
    for (let component = 0; component < value.length; component++) {
        value[component] = Math.round(value[component]);
    }
    return value;
}
function boxStatisticAtIJK(source, reduction, i, j, k, statistic = VoxelStatistics.Average) {
    return valueOfBox(source, resolveReduction(source, reduction), createAccumulators(statistic), i, j, k);
}
function boxAverageAtIJK(source, reduction, i, j, k) {
    return boxStatisticAtIJK(source, reduction, i, j, k, VoxelStatistics.Average);
}
function reduceByBoxStatistic(source, reduction, target, options = {}) {
    const { round = true, statistic = VoxelStatistics.Average } = options;
    const definition = requireVoxelStatistic(statistic);
    const sliceWritten = definition.reduceBySlices?.(source, reduction, target, {
        round,
    });
    if (sliceWritten !== undefined) {
        return sliceWritten;
    }
    const accumulators = { definition, perComponent: [] };
    const resolved = resolveReduction(source, reduction);
    const { dimensions } = resolved;
    const [targetI, targetJ, targetK] = reduction.targetOffset ?? [0, 0, 0];
    let written = 0;
    for (let k = 0; k < dimensions[2]; k++) {
        for (let j = 0; j < dimensions[1]; j++) {
            for (let i = 0; i < dimensions[0]; i++) {
                const value = valueOfBox(source, resolved, accumulators, i, j, k);
                if (value === undefined) {
                    continue;
                }
                target.setAtIJK(targetI + i, targetJ + j, targetK + k, (round ? roundValue(value) : value));
                written++;
            }
        }
    }
    return written;
}
function reduceByBoxAverage(source, reduction, target, options = {}) {
    return reduceByBoxStatistic(source, reduction, target, {
        ...options,
        statistic: VoxelStatistics.Average,
    });
}
export { reduceByBoxStatistic as default, reduceByBoxStatistic, reduceByBoxAverage, boxStatisticAtIJK, boxAverageAtIJK, };
